import { IconeSeta } from '@/components/atoms/Icones/IconeSeta';
import Label from '@/components/atoms/Label';
import {
  ComponentType,
  CSSProperties,
  Dispatch,
  forwardRef,
  Fragment,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as S from './styles';
import { DefaultTheme, useTheme } from 'styled-components';
import Text from '@/components/atoms/Text';
import DropdownMenu, { DropdownMenuItem } from '../DropdownMenu';
import { IconProps } from '@/types/icon';

export type SelectSize = 1 | 2 | 3;
export type SelectVariant = 'surface' | 'soft' | 'ghost';

interface SelectProps {
  $size: SelectSize;
  $variant: SelectVariant;
  $color: string;
  $selectOptions: { id: string | number; label: string }[];
  $label?: string;
  $obrigatorio?: boolean;
  $style?: CSSProperties;
  $inputProps?: object;
  $placeholder: string;
  $name: string;
  $error: string | boolean;
  $value: string;
  // @ts-expect-error Valor Generico
  // eslint-disable-next-line no-undef
  $selecionarOpcao: Dispatch<SetStateAction<T>>;
  $IconStart?: ComponentType<IconProps>;
  styleConteudoDropdown?: CSSProperties;
}

export function getCorIcone({
  theme,
  $variant,
  $color,
  $error,
}: {
  theme: DefaultTheme;
  $variant: string;
  $color: string;
  $error: boolean;
}) {
  if (['ghost', 'soft', 'outline'].includes($variant))
    return theme.colors[$color][$color][11];
  if ($error) return theme.colors.semantic.error[12];
  return theme.colors.neutral.neutral[12];
}

const Select = forwardRef<HTMLInputElement, SelectProps>((props, ref) => {
  const {
    $style,
    $obrigatorio = false,
    $label,
    $color,
    $size,
    $name,
    $variant,
    $error,
    $placeholder,
    $value = '',
    $selectOptions,
    $selecionarOpcao,
    $IconStart,
    $inputProps = {},
    styleConteudoDropdown = {},
  } = props;
  const propsContainer = { $color, $size, $variant, $error };
  const theme = useTheme();

  const [larguraContainer, setLarguraContainer] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const mudancaValorLargura = () => {
      if (containerRef?.current) {
        const { offsetWidth, offsetHeight } = containerRef.current;
        const newSize = { width: offsetWidth, height: offsetHeight };
        setLarguraContainer(newSize.width);
      }
    };

    const mudancaTamanhoObserver = new ResizeObserver(mudancaValorLargura);
    if (containerRef?.current) {
      mudancaTamanhoObserver.observe(containerRef.current);
    }

    return () => {
      mudancaTamanhoObserver.disconnect();
    };
  }, []);

  return (
    <S.ContainerWrapper style={$style}>
      {$label ? (
        <Label
          $size={1}
          $obrigatorio={$obrigatorio}
          $highContrast={false}
          $label={$label}
        />
      ) : null}
      <DropdownMenu
        $size={2}
        $variant="solid"
        $highContrast={false}
        $type="default"
        $sideOffset={3}
        $triggerDropdownMenu={
          <S.Container ref={containerRef} type="button" {...propsContainer}>
            {$IconStart ? (
              <$IconStart
                color={theme.colors[$color][$color][12]}
                size={theme.typography.font.size[5]}
              />
            ) : null}
            <S.Input
              ref={ref}
              name={$name}
              type="text"
              placeholder={$placeholder}
              value={$value}
              $error={!!$error}
              readOnly
              {...$inputProps}
            />
            <IconeSeta
              size={theme.typography.font.size[5]}
              color={getCorIcone({ theme, $color, $variant, $error: !!$error })}
              direcao="baixo"
            />
          </S.Container>
        }
        $contentDropdownMenu={
          <Fragment>
            {$selectOptions?.map((selectOption, index) => (
              <DropdownMenuItem
                $size={2}
                key={`${selectOption.id}_${index}`}
                onSelect={() => $selecionarOpcao(selectOption?.id)}
              >
                {selectOption.label}
              </DropdownMenuItem>
            ))}
          </Fragment>
        }
        style={{
          width: larguraContainer,
          ...styleConteudoDropdown,
        }}
      />
      {$error && typeof $error === 'string' ? (
        <Text
          $size={1}
          $color="error"
          $weight="bold"
          $align="left"
          $highContrast={true}
          $text={$error}
        />
      ) : null}
    </S.ContainerWrapper>
  );
});

Select.displayName = 'Select';

export default Select;
